/**
 * Tabs for the category highlights section (ARIA tabs pattern).
 * Click or ←/→/Home/End on the tab row switches panels. In the theme editor, selecting a tab or
 * tile block shows the tab it belongs to.
 *
 * Tile photos: a tile marked data-auto-image="<category>" has no picture of its own, so when its tab is
 * shown we ask the Shop Products page for that type (it filters on the whole catalog) and use the photo of
 * the first product that has a picture. Results are remembered for the visit (sessionStorage); if nothing is found the
 * stand-in graphic stays.
 */
class CategoryTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    if (!this.tabs.length) return;

    this.tabs.forEach((tab, index) => tab.addEventListener('click', () => this.select(index)));

    this.querySelector('[role="tablist"]').addEventListener('keydown', (event) => {
      const current = this.tabs.indexOf(document.activeElement);
      if (current < 0) return;
      const last = this.tabs.length - 1;
      const next = {
        ArrowRight: current === last ? 0 : current + 1,
        ArrowLeft: current === 0 ? last : current - 1,
        Home: 0,
        End: last,
      }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      this.select(next, { focus: true });
    });

    this.addEventListener('shopify:block:select', (event) => {
      const tabIndex = this.tabs.indexOf(event.target.closest('[role="tab"]'));
      const panelIndex = this.panels.indexOf(event.target.closest('[role="tabpanel"]'));
      const index = tabIndex >= 0 ? tabIndex : panelIndex;
      if (index >= 0) this.select(index);
    });

    this.loadPhotos(this.panels[0]);
  }

  async loadPhotos(panel) {
    if (!panel || panel.dataset.photosLoaded) return;
    panel.dataset.photosLoaded = 'true';
    const tiles = [...panel.querySelectorAll('[data-auto-image]')];
    await Promise.all(tiles.map((tile) => this.loadPhoto(tile)));
  }

  async loadPhoto(tile) {
    const category = tile.dataset.autoImage;
    const key = `te-category-photo-v2:${category}`;
    let url = null;
    try {
      url = sessionStorage.getItem(key);
    } catch (error) {
      url = null;
    }
    if (url === null) {
      url = '';
      try {
        // The Shop Products page filters on the whole catalog; take the first product card with a photo
        // (checking up to three pages, since many products have none yet).
        const base = `/collections/all?filter.p.product_type=${encodeURIComponent(category)}`;
        for (let page = 1; page <= 3 && !url; page += 1) {
          const response = await fetch(`${base}&page=${page}`, { headers: { 'X-Requested-With': 'fetch' } });
          if (!response.ok) break;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const cards = [...doc.querySelectorAll('.catalog__grid > li')];
          if (!cards.length) break;
          const match = cards.map((card) => card.querySelector('.product-card__media img')).find((img) => img && img.getAttribute('src'));
          if (match) url = match.getAttribute('src');
        }
      } catch (error) {
        url = '';
      }
      try {
        sessionStorage.setItem(key, url);
      } catch (error) {
        /* private mode: no caching */
      }
    }
    if (!url) return;
    const media = tile.querySelector('.highlight-tile__media');
    if (!media) return;
    const sized = new URL(url, window.location.origin);
    sized.searchParams.set('width', '480');
    const image = new Image();
    image.className = 'highlight-tile__photo';
    image.alt = '';
    image.src = sized.toString();
    image.addEventListener('load', () => media.replaceChildren(image), { once: true });
  }

  select(index, { focus = false } = {}) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (this.panels[i]) this.panels[i].hidden = !selected;
    });
    this.loadPhotos(this.panels[index]);
    const tab = this.tabs[index];
    if (focus) tab.focus();
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}
customElements.define('category-tabs', CategoryTabs);
